import type { ReactNode } from 'react'
import { needsConsent, reviewConsent } from '@/components/consent/consent'
import { PageSeo } from '@/components/seo/PageSeo'
import { sitePages } from '@/config/pages'
import { siteConfig } from '@/config/site'

/* The privacy policy, set like About: a label on the left and the words on the right. It describes
   what the site actually does: hosting and cookieless analytics on Vercel, email and Calendly, and
   nomination pages and their answers (Supabase, Slack). No cookies are set. */

const { entityName, registeredAddress, office, policyUpdated } = siteConfig.legal

const EmailUs = () => <a href={`mailto:${siteConfig.email}`}>{siteConfig.email}</a>

const Row = ({ id, label, children }: { id?: string; label: string; children: ReactNode }) => (
  <section id={id} className="a2-row">
    <h2 className="x-eyebrow x-eyebrow--ink a2-label">{label}</h2>
    <div className="a2-copy privacy-copy">{children}</div>
  </section>
)

export const PrivacyPage = () => (
  <>
    <PageSeo page={sitePages.privacy} />
    <div className="a2">
      <div className="nav-space" aria-hidden="true" />
      <div className="x-wrap">
        <section className="a2-row a2-row--top" aria-labelledby="privacy-title">
          <p className="x-eyebrow x-eyebrow--ink a2-label">Privacy</p>
          <div className="a2-copy">
            <h1 id="privacy-title" className="a2-title">
              Privacy policy
            </h1>
            <p className="a2-text a2-text--lead">
              This policy explains what personal information United Accountants collects through unitedaccountants.com, why we
              collect it, who we share it with, and the choices you have.
            </p>
            <p className="privacy-updated">Last updated {policyUpdated}</p>
          </div>
        </section>

        <Row label="Who we are">
          <p className="a2-text">
            This website is run by {entityName} (“United”, “we”, “us”), which is responsible for the personal information
            described in this policy. Our registered address is {registeredAddress}, and our office is at {office.street},{' '}
            {office.city}, {office.region} {office.postalCode}. For anything about this policy or your information, email{' '}
            <EmailUs />.
          </p>
        </Row>

        <Row label="What we collect">
          <h3>When you browse</h3>
          <p className="a2-text">
            Our hosting provider, Vercel, processes technical data such as your IP address, browser and the pages you request, to
            deliver the site and keep it secure. We count visits with Vercel Web Analytics, which uses no cookies and does not
            identify you.
          </p>
          <h3>When you contact us or book a call</h3>
          <p className="a2-text">
            When you email us, we receive your email address and what you write. Booking a call opens Calendly, a separate
            service: what you enter there is handled under Calendly’s privacy policy and shared with us so we can meet.
          </p>
          <h3>Nomination pages</h3>
          <p className="a2-text">
            Nomination pages are private pages we prepare for people nominated to our community of firm owners. They show
            publicly available professional information about the nominee, such as name, role, firm, location and a profile
            photo. If you answer a nomination, we receive your decision, the email address you give and anything you write, and
            keep them with the nomination.
          </p>
        </Row>

        <Row label="How we use it">
          <ul className="privacy-list">
            <li>To run the website, keep it secure and understand how it is used.</li>
            <li>To reply to you and arrange the conversations you ask for.</li>
            <li>To run our community of firm owners and contact the nominees who accept.</li>
          </ul>
          <p className="a2-text">We do not sell your personal information.</p>
        </Row>

        <Row label="Legal bases">
          <p className="a2-text">
            Where the GDPR or the UK GDPR applies, we rely on our legitimate interests to run and secure the site, count visits
            without cookies, answer business enquiries and prepare nominations from public professional information, and on
            steps you ask us to take before an agreement, such as a meeting.
          </p>
        </Row>

        <Row label="Who we share it with">
          <p className="a2-text">
            Service providers that process information for us: Vercel (hosting and analytics), Google Workspace (our email),
            Supabase (stores answers to nominations), Slack (tells our team when a nomination is answered) and Calendly (when
            you book a call). We also share information when the law requires it, and with a buyer or successor if United is
            involved in a merger or acquisition.
          </p>
        </Row>

        <Row label="Transfers">
          <p className="a2-text">
            Our team works in the United States and Estonia, and our providers may process information in other countries. When
            information leaves the UK or the European Economic Area, we rely on appropriate safeguards such as standard
            contractual clauses.
          </p>
        </Row>

        <Row label="How long we keep it">
          <p className="a2-text">
            Only as long as we need it for the purposes above, then we delete or anonymise it. You can ask us to remove a
            nomination page, or your answer to one, at any time.
          </p>
        </Row>

        <Row id="cookies" label="Cookies">
          <p className="a2-text">
            We do not use cookies. Your browser keeps one preference on your device: whether you paused the animations. It is
            needed for the site to work as you asked and is never sent to us.
          </p>
          {needsConsent ? (
            <button type="button" className="btn btn-line a2-btn privacy-settings" onClick={reviewConsent}>
              Cookie settings
            </button>
          ) : null}
        </Row>

        <Row label="Your rights">
          <p className="a2-text">
            Depending on where you live, including under the GDPR, the UK GDPR and the privacy laws of California and other US
            states, you can ask to access, correct or delete your information, object to or restrict how we use it, receive a
            copy of it, and withdraw your consent at any time. Email <EmailUs /> and we will reply within one month. We will not
            treat you differently for using these rights. You can also complain to your data protection authority.
          </p>
        </Row>

        <Row label="Children">
          <p className="a2-text">This website is for business owners and professionals, and is not directed at children.</p>
        </Row>

        <Row label="Changes">
          <p className="a2-text">
            If we change this policy we will update it here and change the date at the top.
          </p>
        </Row>
      </div>
    </div>
  </>
)
